import type {
  PaymentInfo,
  PaymentInfoCurrentPlan,
  PricingPlan,
} from "./pricing-plan.type";

/** Plans with duration >= 365 days are treated as annual billing. */
export const ANNUAL_PLAN_DURATION_THRESHOLD = 365;

export const isAnnualPricingPlanDuration = (duration: number | string): boolean =>
  Number(duration) >= ANNUAL_PLAN_DURATION_THRESHOLD;

export const getSubscriptionProvider = (
  info?: PaymentInfo | null,
): string | null => {
  const raw = info?.subscription_provider;
  if (raw == null) return null;
  const value = String(raw).trim().toLowerCase();
  return value === "" ? null : value;
};

export const isFreePricingPlan = (
  plan?: PaymentInfoCurrentPlan | null,
): boolean => {
  if (!plan) return true;
  return plan.name.trim().toLowerCase() === "free";
};

/** Store (App Store / Play) is billing this user. Website must not buy or cancel. */
export const isStoreBilledSubscription = (
  info?: PaymentInfo | null,
): boolean => {
  if (getSubscriptionProvider(info) !== "revenuecat") return false;
  return !isFreePricingPlan(info?.current_plan);
};

export const getStoreManagementUrl = (
  info?: PaymentInfo | null,
): string | null => {
  const url = info?.management_url?.trim();
  return url ? url : null;
};

/** User may start a free trial right now (server-side rule, see docs/free-trial-design.md). */
export const isTrialEligible = (info?: PaymentInfo | null): boolean => {
  if (!info?.trial) return false;
  if (isStoreBilledSubscription(info)) return false;
  return info.trial.eligible === true;
};

/** A started trial that has not been charged yet (auto-renew on or off). */
export const isTrialActive = (info?: PaymentInfo | null): boolean => {
  const status = info?.trial?.status;
  return status === "active" || status === "cancelled";
};

/** Free-trial length for every paid plan (mirrors the backend constant). */
export const TRIAL_DAYS = 14;

/** Trial length a plan offers: the fixed length for paid plans, 0 for free plans. */
export const getPlanTrialDays = (plan?: Pick<PricingPlan, "price"> | null): number => {
  if (!plan) return 0;
  return Number(plan.price) > 0 ? TRIAL_DAYS : 0;
};

/** The plan can be started as a trial by this user. */
export const canStartTrialForPlan = (
  info: PaymentInfo | null | undefined,
  plan: Pick<PricingPlan, "price"> | null | undefined,
): boolean => isTrialEligible(info) && getPlanTrialDays(plan) > 0;

/**
 * A website-billed renewal failed and is being retried; the user keeps the plan for now.
 * Store-billed subscriptions are handled by the App Store / Play and never show this.
 */
export const isPaymentPastDue = (info?: PaymentInfo | null): boolean => {
  if (!info?.billing) return false;
  if (getSubscriptionProvider(info) === "revenuecat") return false;
  if (isFreePricingPlan(info.current_plan)) return false;
  return info.billing.status === "past_due";
};
